import { FunctionComponent, useCallback, useEffect, useState } from "react";
import { CheckIcon, CloseIcon } from "@form/components/common/icons";
import useAccount from "@/hooks/useAccount";
import useVerifyClaim from "@/hooks/useVerifyClaim";
import { Collapse, Select, Tooltip } from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import { ArrowDownIcon } from "@/components/common/icons";
import { QuestionIcon } from "@form/components/common/icons";
import useToggleStatus from "@/hooks/useToggleStatus";
import useRequest from "@/hooks/useRequest";
import { GetValidatorResult } from "@/types/network";
import { trimAddr1 } from "@/utils/format";
import useMintReferral from "@/hooks/useMintReferral";
import { toast } from "react-toastify";

const SelectValidatorStep: FunctionComponent<{
  chainId: number;
}> = ({ chainId }) => {
  const [selectorOpen, { open: openSelector, close: closeSelector }] =
    useDisclosure(false);
  const { result, changeVerifyState, changeForm, form } =
    useVerifyClaim("validator");

  const { validatorId } = useMintReferral(chainId);
  const [disableSelect, setDisableSelect] = useState(false);

  const [query, setQuery] = useState("");

  useEffect(() => {
    setDisableSelect(!!validatorId && validatorId !== "1");
    if (validatorId) {
      changeForm(validatorId);

      setTimeout(() => {
        setQuery(String(validatorId));
      }, 100);
    }
  }, [validatorId]);

  const { data: validatorList, isLoading: isValidatorListLoading } = useRequest<
    {
      name: string | null;
      ownerAddress: string;
      validatorTokenId: string;
    }[]
  >(
    {
      function: "validator/getValidatorList",
      data: {
        query,
        version: "v1",
      },
      header: {
        endpoint: "network",
      },
    },
    {
      onSuccess(data) {
        if (validatorId && validatorId === form.validator) {
          const exist = data.find((item: any) => {
            return String(item.validatorTokenId) === String(validatorId);
          });
          if (!exist) {
            setDisableSelect(false);
            changeForm(undefined);
            setQuery("");
            toast.info(
              `Validator id ${validatorId} not valid, please select other`
            );
          }
        }
      },
    }
  );

  const validatorSelectList = [
    {
      label: "I don’t have a validator",
      value: "empty",
    },
  ].concat(
    (validatorList || []).map((item) => {
      let name = item.name;
      if (item.validatorTokenId === "1") {
        name = "OKX";
      }
      return {
        label: `#${item.validatorTokenId} ${name || ""}`,
        value: String(item.validatorTokenId),
      };
    })
  );

  const { data: validator } = useRequest<GetValidatorResult>(
    form.validator && form.validator !== "empty" && selectorOpen
      ? {
          function: "validator/getValidator",
          data: {
            validatorTokenId: parseInt(form.validator),
            version: "v1",
          },
          header: {
            endpoint: "network",
          },
        }
      : null
  );

  const { claimerMintToggleStatus, toggle } = useToggleStatus(
    "claimerMintToggleStatus"
  );

  const opened = claimerMintToggleStatus.validator;

  const toggleCard = useCallback(() => {
    toggle("validator");
  }, [toggle]);

  const verifyed = result["validator"];

  useEffect(() => {
    changeVerifyState(typeof form.validator !== "undefined");
  }, [form.validator]);

  return (
    <div
      className={`claim-step-card  ${
        opened ? "border-primary bg-[rgba(235,243,255,0.3)]" : ""
      }`}
    >
      <div className="flex  cursor-pointer" onClick={toggleCard}>
        <CheckIcon
          className={`w-5 h-5 ${verifyed ? "fill-green" : ""}`}
        ></CheckIcon>
        <div className="font-semibold text-dark1 ml-2">
          <div>Select validator</div>
          <div
            className={`text-gray1 font-normal text-sm mt-1 ${
              opened ? "block" : "hidden"
            }`}
          >
            Select or search for your validator
          </div>
        </div>
        <div className="flex-1"></div>
        <ArrowDownIcon
          className={`transition-all self-center ${opened ? "rotate-180" : ""}`}
        ></ArrowDownIcon>
      </div>
      <Collapse in={opened}>
        <div
          className="mt-4 pl-7 cursor-auto"
          onClick={(e) => {
            e.stopPropagation();
          }}
        >
          <Select
            placeholder="Search by name or validator id"
            data={validatorSelectList}
            classNames={{
              input: "rounded-lg border-[#E7E7E7] hover:border-primary",
              dropdown: "custom-flip",
            }}
            value={form.validator}
            onFocus={openSelector}
            onChange={(value) => {
              console.log("value", value);
              changeForm(value);
            }}
            onSearchChange={(q) => {
              if (q.indexOf("#") === 0) {
                setQuery(q.replace(/^#/, "").split(" ")[0]);
              } else {
                setQuery(q);
              }
            }}
            filter={() => {
              return true;
            }}
            searchable
            disabled={disableSelect}
            size="md"
            rightSection={
              <ArrowDownIcon
                className={`transition-all self-center ${
                  selectorOpen ? "rotate-180" : ""
                }`}
              ></ArrowDownIcon>
            }
          />
          {validator && (
            <div className="mt-2 flex px-4 gap-8 flex-wrap sm:gap-[60px]">
              <div className="text-dark2 text-xs">
                <div className="font-semibold">Address</div>
                <div className="mt-1">{trimAddr1(validator.ownerAddress)}</div>
              </div>
              {/* <div className="text-dark2 text-xs">
                <div className="font-semibold flex items-center">
                  <span>Validator level</span>
                </div>
                <div className="mt-1">Tier {validator.tier}</div>
              </div> */}
              <div className="text-dark2 text-xs">
                <div className="font-semibold">Teammates</div>
                <div className="mt-1">{validator.referralNum}</div>
              </div>
            </div>
          )}
        </div>
      </Collapse>
    </div>
  );
};

export default SelectValidatorStep;
